<template>
  <div class="english-central-container">
    <!-- <h1 class="page-title">English Central</h1> -->
    <div class="row col-12 justify-content-center justify-content-md-center">
      <img
        src="@/assets/img/EnglishCentral-logo.png"
        alt="English Central Logo"
        class="english-central-logo"
        style="width: 50%; height: 50%"
      />
    </div>

    <!-- 加載指示器 -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <h5 class="text-white">Loading content...</h5>
    </div>

    <div v-if="error" class="error-message">
      <p>{{ error }}</p>
      <button @click="fetchData" class="retry-button">Retry</button>
    </div>

    <div v-if="!loading && !error" class="content-container">
      <!-- 選項卡導航 -->
      <div class="tabs-container">
        <div
          class="tab font-weight-bold"
          :class="{ active: activeTab === 'roleplays' }"
          @click="activeTab = 'roleplays'"
        >
          Role Plays
        </div>
        <div
          class="tab font-weight-bold"
          :class="{ active: activeTab === 'dialogs' }"
          @click="activeTab = 'dialogs'"
        >
          Dialogs
        </div>
        <!-- 新增進度選項卡 -->
        <!-- <div
          class="tab font-weight-bold"
          :class="{ active: activeTab === 'progress' }"
          @click="activeTab = 'progress'"
        >
          📈 Progress
        </div> -->
      </div>

      <!-- 角色扮演對話區塊 -->
      <div class="content-section" v-show="activeTab === 'roleplays'">
        <div class="filters">
          <div class="filter-group">
            <label class="font-weight-bold mb-0">Difficulty:</label>
            <select
              v-model="rolePlaysFilters.difficultyLevels"
              @change="fetchRolePlays"
            >
              <option value="1">Beginner</option>
              <option value="2">Intermediate</option>
              <option value="3">Advanced</option>
              <option value="1,2">Beginner & Intermediate</option>
              <option value="1,2,3">All Levels</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="font-weight-bold mb-0">Limit:</label>
            <select
              v-model.number="rolePlaysFilters.limit"
              @change="fetchRolePlays"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
            </select>
          </div>
        </div>

        <div class="cards-container">
          <div
            v-for="(rolePlay, index) in uniqueRolePlays"
            :key="'role-' + rolePlay.id"
            class="content-card"
          >
            <div class="card-image">
              <img :src="getRolePlayThumbnail(rolePlay)" alt="Thumbnail" />
              <div
                class="difficulty-badge"
                :class="'level-' + rolePlay.difficultyLevel"
              >
                {{ getDifficultyLabel(rolePlay.difficultyLevel) }}
              </div>
            </div>
            <div class="card-content">
              <div class="card-content-roleplay">
                <h3>{{ rolePlay.title }}</h3>
                <div class="card-meta">
                  <span class="difficulty">
                    Level: {{ getDifficultyLabel(rolePlay.difficultyLevel) }}
                  </span>
                  <span class="duration" v-if="rolePlay.duration">
                    {{ formatDuration(rolePlay.duration) }}
                  </span>
                </div>
                <p class="description">
                  {{ rolePlay.description }}
                </p>

                <div class="roles-container">
                  <div class="role-item">
                    <span class="role-label">Your Role:</span>
                    <span class="role-name">{{
                      rolePlay.learnerCharacterName
                    }}</span>
                  </div>
                  <div class="role-item">
                    <span class="role-label">MiMi's Role:</span>
                    <span class="role-name">{{
                      rolePlay.mimiCharacterName
                    }}</span>
                  </div>
                </div>
              </div>

              <div class="card-footer">
                <button
                  class="view-details-btn"
                  @click="showRolePlayDetails(rolePlay.id)"
                >
                  View Details
                </button>
                <button class="start-btn" @click="startRolePlay(rolePlay)">
                  Start
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="uniqueRolePlays.length === 0" class="no-results">
          No role plays found.
        </div>
      </div>

      <!-- 一般對話區塊 -->
      <div class="content-section" v-show="activeTab === 'dialogs'">
        <div class="filters">
          <div class="filter-group">
            <label class="font-weight-bold mb-0">Difficulty:</label>
            <select v-model="dialogsFilters.difficulty" @change="fetchDialogs">
              <option value="1">Beginner</option>
              <option value="2">Intermediate</option>
              <option value="3">Advanced</option>
              <option value="1,2">Beginner & Intermediate</option>
              <option value="1,2,3">All Levels</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="font-weight-bold mb-0">Page Size:</label>
            <select
              v-model.number="dialogsFilters.pageSize"
              @change="fetchDialogs"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>

        <!-- 進度加載指示器 -->
        <div v-if="dialogDetailedLoading" class="progress-loading">
          <div class="spinner-small"></div>
          <span>正在檢查 Dialog 學習進度...</span>
        </div>

        <div class="cards-container">
          <div
            v-for="dialog in dialogsWithProgress"
            :key="'dialog-' + dialog.dialogID"
            class="content-card dialog-card"
            :class="{
              'has-progress': dialog.hasProgress,
              completed: dialog.isCompleted,
              'no-progress': !dialog.hasProgress && dialogProgressMap.size > 0,
            }"
          >
            <div class="card-image">
              <img
                :src="dialog.demoPictureURL || dialog.seriesThumbnailURL"
                :alt="dialog.title"
              />
              <div
                class="difficulty-badge"
                :class="'level-' + dialog.difficulty"
              >
                {{ getDifficultyLabel(dialog.difficulty) }}
              </div>
              <div v-if="dialog.promotionalDialog" class="promotional-badge">
                Premium
              </div>

              <!-- 新增：進度狀態標記 - 只顯示已完成和有意義的進度 -->
              <div
                v-if="
                  dialogProgressMap.size > 0 &&
                  (dialog.isCompleted ||
                    (dialog.hasProgress && dialog.completionRate > 0))
                "
                class="progress-badge"
              >
                <span v-if="dialog.isCompleted" class="completed-badge"
                  >✅ 已完成</span
                >
                <span
                  v-else-if="dialog.hasProgress && dialog.completionRate > 0"
                  class="in-progress-badge"
                  >🔄 {{ dialog.completionRate }}%</span
                >
              </div>
            </div>

            <div class="card-content card-content-dialog">
              <h3 class="card-title">{{ dialog.title }}</h3>
              <div class="card-meta">
                <span class="difficulty">
                  Level: {{ getDifficultyLabel(dialog.difficulty) }}
                </span>
                <span class="duration" v-if="dialog.duration">
                  {{ dialog.duration }}
                </span>
              </div>

              <!-- 新增：進度詳情 - 顯示所有 Dialog 的狀態 -->
              <div
                v-if="dialogProgressMap.size > 0"
                class="dialog-progress-info"
              >
                <!-- 未開始狀態 -->
                <div v-if="!dialog.hasProgress" class="no-progress-status">
                  <span class="status-text">📝 尚未開始學習</span>
                </div>

                <!-- 有進度狀態 -->
                <div v-else>
                  <div class="progress-bar-mini">
                    <div
                      class="progress-fill-mini"
                      :style="{ width: dialog.completionRate + '%' }"
                    ></div>
                  </div>
                  <div class="progress-stats">
                    <span class="stat">{{ dialog.completionRate }}% 完成</span>
                    <span v-if="dialog.stats" class="stat"
                      >{{ dialog.stats.earnedPoints }}/{{
                        dialog.stats.totalPoints
                      }}
                      分</span
                    >
                    <span
                      v-if="
                        dialog.stats &&
                        dialog.stats.accuracy > 0 &&
                        dialog.stats.totalAnswers > 0
                      "
                      class="stat"
                      >準確率: {{ dialog.stats.accuracy }}% ({{
                        dialog.stats.correctAnswers
                      }}/{{ dialog.stats.totalAnswers }})</span
                    >
                  </div>
                </div>
              </div>

              <div class="card-footer">
                <!-- <button class="view-details-btn" @click="openDialogURL(dialog)">
                  View on EC
                </button> -->
                <button
                  class="start-btn"
                  @click="startDialog(dialog)"
                  :class="{
                    'continue-btn': dialog.hasProgress && !dialog.isCompleted,
                  }"
                >
                  {{
                    dialog.isCompleted
                      ? "Review"
                      : dialog.hasProgress
                      ? "Continue"
                      : "Start"
                  }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="dialogs.length === 0" class="no-results">
          No dialogs found.
        </div>
      </div>

      <!-- 新增：進度區塊 -->
      <div class="content-section" v-show="activeTab === 'progress'">
        <div class="filters">
          <div class="filter-group">
            <label class="font-weight-bold mb-0">Report Types:</label>
            <select
              v-model="progressFilters.reportTypes"
              @change="fetchProgress"
            >
              <option value="overallXP,courseProgress">
                Overall XP & Course Progress
              </option>
              <option value="overallXP">Overall XP Only</option>
              <option value="courseProgress">Course Progress Only</option>
            </select>
          </div>
          <div class="filter-group">
            <!-- <button class="btn" @click="fetchActivitySummary">
              📊 Activity Summary
            </button> -->
            <button class="btn" @click="getCompleteProgress">
              🚀 Complete Progress
            </button>
            <!-- <button class="btn" @click="getCurrentUserProgress">
              👤 My Progress (Old)
            </button> -->
          </div>
        </div>

        <!-- 詳細進度顯示 -->
        <div v-if="detailedProgress" class="detailed-progress-section">
          <h4>📊 詳細學習進度</h4>

          <!-- 用戶基本信息 -->
          <!-- <div v-if="detailedProgress.user_info" class="user-basic-info">
            <h5>👤 用戶信息</h5>
            <div class="user-info-grid">
              <div class="info-item">
                <span class="label">姓名:</span>
                <span class="value">{{ detailedProgress.user_info.name }}</span>
              </div>
              <div class="info-item">
                <span class="label">Account ID:</span>
                <span class="value">{{
                  detailedProgress.user_info.accountID
                }}</span>
              </div>
              <div class="info-item">
                <span class="label">平台 ID:</span>
                <span class="value">{{
                  detailedProgress.user_info.platform_id ||
                  detailedProgress.user_info.partnerAccountID ||
                  "N/A"
                }}</span>
              </div>
              <div class="info-item">
                <span class="label">總經驗值:</span>
                <span class="value">{{
                  detailedProgress.user_info.overallXP || "N/A"
                }}</span>
              </div>
            </div>
          </div> -->

          <!-- Dialog 進度總覽 -->
          <!-- <div v-if="dialogAnalysis" class="dialog-analysis-section">
            <h5>🎯 Dialog 學習進度總覽</h5>
            <div class="analysis-stats">
              <div class="stat-card">
                <div class="stat-number">{{ dialogAnalysis.totalDialogs }}</div>
                <div class="stat-label">總 Dialog 數</div>
              </div>
              <div class="stat-card completed">
                <div class="stat-number">
                  {{ dialogAnalysis.completedDialogs }}
                </div>
                <div class="stat-label">已完成</div>
              </div>
              <div class="stat-card in-progress">
                <div class="stat-number">
                  {{ dialogAnalysis.inProgressDialogs }}
                </div>
                <div class="stat-label">進行中</div>
              </div>
              <div class="stat-card completion-rate">
                <div class="stat-number">
                  {{ dialogAnalysis.completionRate }}%
                </div>
                <div class="stat-label">完成率</div>
              </div>
              <div class="stat-card points">
                <div class="stat-number">{{ dialogAnalysis.earnedPoints }}</div>
                <div class="stat-label">獲得積分</div>
              </div>
              <div class="stat-card points-rate">
                <div class="stat-number">
                  {{ dialogAnalysis.pointsPercentage }}%
                </div>
                <div class="stat-label">積分完成率</div>
              </div>
            </div>
          </div> -->

          <!-- 最近 Dialog 活動 -->
          <div
            v-if="recentDialogActivity.length > 0"
            class="recent-dialog-activity"
          >
            <h5>⏰ 最近 Dialog 活動</h5>
            <div class="activity-list">
              <div
                v-for="activity in recentDialogActivity"
                :key="activity.dialogID"
                class="activity-item"
              >
                <div class="activity-header">
                  <h6 class="activity-title">{{ activity.dialogTitle }}</h6>
                  <span class="activity-id">ID: {{ activity.dialogID }}</span>
                </div>
                <div class="activity-progress">
                  <div class="progress-bar">
                    <div
                      class="progress-fill"
                      :style="{ width: activity.progressPercentage + '%' }"
                    ></div>
                  </div>
                  <span class="progress-text"
                    >{{ activity.progressPercentage }}%</span
                  >
                </div>
                <div class="activity-meta">
                  <span class="activity-points"
                    >{{ activity.earnedPoints }} 積分</span
                  >
                  <span class="activity-date">{{
                    new Date(activity.lastActivity).toLocaleDateString("zh-TW")
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="progress-details">
            <!-- <div class="progress-summary">
              <h5>總體活動摘要</h5>
              <div class="summary-stats">
                <div class="stat-item">
                  <span class="label">總積分:</span>
                  <span class="value">{{
                    detailedProgress.total_points || "N/A"
                  }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">已完成活動:</span>
                  <span class="value">{{
                    detailedProgress.completed_activities || "N/A"
                  }}</span>
                </div>
                <div class="stat-item">
                  <span class="label">進行中活動:</span>
                  <span class="value">{{
                    detailedProgress.in_progress_activities || "N/A"
                  }}</span>
                </div>
              </div>
            </div> -->

            <div
              v-if="detailedProgress.dialog_progress"
              class="dialog-progress"
            >
              <h5>對話進度</h5>
              <div class="dialog-list">
                <div
                  v-for="dialog in detailedProgress.dialog_progress"
                  :key="dialog.id"
                  class="dialog-item"
                >
                  <h6>{{ dialog.title }}</h6>
                  <div class="dialog-stats">
                    <span
                      >完成度: {{ dialog.completion_percentage || 0 }}%</span
                    >
                    <span>活動數: {{ dialog.activities_count || 0 }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="detailedProgress.course_progress"
              class="course-progress"
            >
              <h5>課程進度</h5>
              <div class="course-list">
                <div
                  v-for="course in detailedProgress.course_progress"
                  :key="course.id"
                  class="course-item"
                >
                  <h6>{{ course.title }}</h6>
                  <div class="course-stats">
                    <span
                      >完成度: {{ course.completion_percentage || 0 }}%</span
                    >
                    <span>章節數: {{ course.chapters_count || 0 }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="progressLoading" class="loading-container">
          <div class="spinner"></div>
          <h5 class="text-white">Loading progress data...</h5>
        </div>

        <!-- 生成中提示 -->
        <div v-else-if="progressMessage" class="generating-message">
          <div class="generating-icon">⏳</div>
          <h5>{{ progressMessage }}</h5>
          <p>報告正在生成中，請稍後重試...</p>
          <button @click="fetchProgress" class="retry-button">重試</button>
        </div>

        <div v-else-if="progressError" class="error-message">
          <p>{{ progressError }}</p>
          <button @click="fetchProgress" class="retry-button">Retry</button>
        </div>

        <!-- 活動摘要顯示 -->
        <div v-else-if="activitySummary" class="activity-summary-section">
          <h3>📊 活動摘要</h3>
          <div class="summary-cards">
            <div class="summary-card">
              <span class="label">總積分</span>
              <span class="value">{{
                activitySummary.totalActivityPoints
              }}</span>
            </div>
            <div class="summary-card">
              <span class="label">已完成活動</span>
              <span class="value">{{
                activitySummary.activitiesCompleted
              }}</span>
            </div>
            <div class="summary-card">
              <span class="label">進行中活動</span>
              <span class="value">{{
                activitySummary.activitiesStarted -
                activitySummary.activitiesCompleted
              }}</span>
            </div>
          </div>
        </div>

        <!-- 我的英語視頻顯示 -->
        <div v-else-if="myVideos.length > 0" class="my-videos-section">
          <h3>🎥 我的英語視頻</h3>
          <div class="video-filters">
            <select
              v-model="videoFilters.progress"
              @change="fetchMyEnglishVideos"
            >
              <option value="all">全部</option>
              <option value="inProgress">進行中</option>
              <option value="GoLiveReady">準備就緒</option>
            </select>
          </div>
          <div class="video-grid">
            <div v-for="video in myVideos" :key="video.id" class="video-card">
              <h4>{{ video.title }}</h4>
              <div class="video-progress">
                <div class="progress-bar">
                  <div
                    class="progress-fill"
                    :style="{ width: video.progress + '%' }"
                  ></div>
                </div>
                <span class="progress-text">{{ video.progress }}%</span>
              </div>
              <div class="video-meta">
                <span>難度: {{ video.difficulty }}</span>
                <span>時長: {{ video.duration }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 學生課程進度顯示 -->
        <div
          v-else-if="studentCourseProgress.length > 0"
          class="student-course-section"
        >
          <h3>📚 學生課程進度</h3>
          <div class="course-grid">
            <div
              v-for="course in studentCourseProgress"
              :key="course.courseID"
              class="course-card"
            >
              <h4>{{ course.courseName }}</h4>
              <div class="course-progress">
                <div class="progress-bar">
                  <div
                    class="progress-fill"
                    :style="{ width: course.progress * 100 + '%' }"
                  ></div>
                </div>
                <span class="progress-text"
                  >{{ Math.round(course.progress * 100) }}%</span
                >
              </div>
              <div class="course-meta">
                <span
                  >開始日期:
                  {{ new Date(course.startDate).toLocaleDateString() }}</span
                >
                <span>會話時間: {{ course.sessionTimeKey }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="progress-grid">
          <div
            v-for="userProgress in progressData"
            :key="userProgress.accountID"
            class="progress-card"
          >
            <div class="user-info">
              <h3>{{ userProgress.name }}</h3>
              <p>Account ID: {{ userProgress.accountID }}</p>
              <p>
                Date Added:
                {{ new Date(userProgress.dateAdded).toLocaleDateString() }}
              </p>
            </div>

            <div class="progress-reports">
              <div
                v-for="report in userProgress.reports"
                :key="report.reportType"
                class="progress-report"
              >
                <h4 v-if="report.reportType === 'courseProgress'">
                  📚 Course Progress
                </h4>
                <h4 v-else-if="report.reportType === 'overallXP'">
                  🏆 Overall XP
                </h4>

                <div v-if="report.courseCompletions" class="course-completions">
                  <div
                    v-for="course in report.courseCompletions"
                    :key="course.courseID"
                    class="course-item"
                  >
                    <div class="course-name">
                      {{ course.courseName }} (ID: {{ course.courseID }})
                    </div>
                    <div class="progress-bar">
                      <div
                        class="progress-fill"
                        :style="{ width: course.percentage * 100 + '%' }"
                      ></div>
                    </div>
                    <div class="progress-text">
                      {{ (course.percentage * 100).toFixed(1) }}%
                    </div>
                  </div>
                </div>

                <div v-if="report.points" class="xp-points">
                  <div class="xp-value">{{ report.points }}</div>
                  <div>Total Experience Points</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="
            progressData.length === 0 &&
            !progressLoading &&
            !progressError &&
            !progressMessage &&
            !activitySummary &&
            myVideos.length === 0 &&
            studentCourseProgress.length === 0
          "
          class="no-results"
        >
          No progress data found.
        </div>
      </div>
    </div>

    <!-- 詳細信息模態框 -->
    <div
      v-if="selectedRolePlay !== null"
      class="modal-overlay"
      @click="closeModal"
    >
      <div class="modal-content" @click.stop>
        <button class="close-modal" @click="closeModal">&times;</button>

        <template v-if="selectedRolePlayData">
          <h2>{{ selectedRolePlayData.title }}</h2>
          <div
            class="difficulty-tag"
            :class="'level-' + selectedRolePlayData.difficultyLevel"
          >
            {{ getDifficultyLabel(selectedRolePlayData.difficultyLevel) }}
          </div>

          <div class="modal-img-container">
            <img
              :src="getRolePlayThumbnail(selectedRolePlayData)"
              alt="Role Play"
            />
          </div>

          <div class="modal-info">
            <h3>Description</h3>
            <p>{{ selectedRolePlayData.description }}</p>

            <h3>Scenario</h3>
            <p>{{ selectedRolePlayData.scenario }}</p>

            <h3>Your Instructions</h3>
            <p>{{ selectedRolePlayData.learnerPrompt }}</p>

            <div class="roles-detail">
              <div class="role-detail-item">
                <h3>Your Role</h3>
                <p>{{ selectedRolePlayData.learnerCharacterName }}</p>
              </div>

              <div class="role-detail-item">
                <h3>MiMi's Role</h3>
                <p>{{ selectedRolePlayData.mimiCharacterName }}</p>
              </div>
            </div>

            <div class="first-message">
              <h3>MiMi's First Message</h3>
              <p class="message-bubble">
                {{ selectedRolePlayData.firstMimiMessage }}
              </p>
            </div>

            <div
              class="candos-section"
              v-if="
                selectedRolePlayData.candos &&
                selectedRolePlayData.candos.length > 0
              "
            >
              <h3>Learning Objectives</h3>
              <div
                class="cando-item"
                v-for="(cando, idx) in selectedRolePlayData.candos"
                :key="'cando-' + idx"
              >
                <p class="cando-name">{{ cando.name }}</p>

                <ul class="objectives-list">
                  <li
                    v-for="objective in getSortedObjectives(cando.objectives)"
                    :key="'obj-' + objective.id"
                  >
                    {{ objective.description }}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="modal-actions">
            <button
              class="start-btn-large"
              @click="startRolePlay(selectedRolePlayData)"
            >
              Start Role Play
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import {
  getEnglishCentralRolePlays,
  getEnglishCentralDialogs,
  getEnglishCentralToken,
  getPartnerProgress,
  getUserProgressReport,
  getCourseProgress,
  getUserCourseProgress,
  getDialogProgress,
  getDialogActivities,
  getActivitySummary,
  getMyEnglishVideos,
  getStudentCourseProgress,
  getUserDetailedProgress,
  getUserInfoUnified,
  getUserProgressUnified,
  analyzeDialogProgress,
  getDialogById,
  getDialogTitleById,
  getMultipleDialogInfo,
  getRecentDialogActivity,
  handleApiError,
  getDialogDetailedProgress,
  getMultipleDialogDetailedProgress,
  isDialogCompleted,
  calculateDialogCompletionRate,
  getDialogDetailedStats,
  analyzeDialogProgressResults,
  getDialogsWithProgress,
  getDialogsWithProgressDetails,
  getUserActivitySummary,
  getCompleteUserProgress,
} from "@/service/apis/english_central";
import { useUserInfoStore } from "@/store/modules/userInfo";
const userInfo = useUserInfoStore();
// 使用 router 進行導航
const router = useRouter();

// 狀態變數
const loading = ref(true);
const error = ref(null);
const rolePlays = ref([]);
const dialogs = ref([]);
const selectedRolePlay = ref(null);
const activeTab = ref("roleplays"); // 默認顯示角色扮演選項卡

// 添加 token 相關狀態變數
const authToken = ref(null);
const partnerKey = ref(null);
const tokenError = ref(null);
const tokenExpiryTime = ref(null); // 新增：儲存 token 過期時間

// 新增：進度相關狀態變數
const progressData = ref([]);
const progressLoading = ref(false);
const progressError = ref(null);
const progressMessage = ref(null); // 新增：用於顯示生成中消息
const progressFilters = ref({
  reportTypes: "overallXP,courseProgress",
  page: 0,
  pageSize: 200,
});

// 新增：活動摘要相關狀態變數
const activitySummary = ref(null);
const activitySummaryLoading = ref(false);
const activitySummaryError = ref(null);

// 新增：我的英語視頻相關狀態變數
const myVideos = ref([]);
const myVideosLoading = ref(false);
const myVideosError = ref(null);
const videoFilters = ref({
  page: 0,
  pageSize: 12,
  progress: "all",
  sortBy: 1,
});

// 新增：學生課程進度相關狀態變數
const studentCourseProgress = ref([]);
const studentCourseProgressLoading = ref(false);
const studentCourseProgressError = ref(null);

const detailedProgress = ref(null);
const detailedProgressLoading = ref(false);
const detailedProgressError = ref(null);

// Dialog 進度分析
const dialogAnalysis = ref(null);
const recentDialogActivity = ref([]);

// Dialog 詳細進度狀態
const dialogDetailedProgress = ref([]);
const dialogDetailedLoading = ref(false);
const dialogDetailedError = ref(null);
const dialogProgressAnalysis = ref(null);

// Dialog 進度映射表（以 dialogID 為 key）
const dialogProgressMap = ref(new Map());

// 測試用的 Dialog 列表
const testDialogList = ref([
  { dialogID: 45464, title: "Climate Action" },
  { dialogID: 43587, title: "How Migrants Have Changed Australia" },
  { dialogID: 27727, title: "Hillary's Message to Girls" },
  { dialogID: 44423, title: "Make Memories with AI" },
  { dialogID: 45590, title: "Working Together on a School Project" },
]);

// 計算屬性：去除重複項目
const uniqueRolePlays = computed(() => {
  const seen = new Set();
  return rolePlays.value.filter((item) => {
    const duplicate = seen.has(item.id);
    seen.add(item.id);
    return !duplicate;
  });
});

// 計算屬性：獲取選中的角色扮演數據
const selectedRolePlayData = computed(() => {
  if (selectedRolePlay.value === null) return null;
  return (
    uniqueRolePlays.value.find((rp) => rp.id === selectedRolePlay.value) || null
  );
});

// 計算屬性：帶進度信息的 Dialogs
const dialogsWithProgress = computed(() => {
  return dialogs.value.map((dialog) => {
    const progressInfo = dialogProgressMap.value.get(dialog.dialogID);
    return {
      ...dialog,
      progressInfo: progressInfo || null,
      hasProgress: progressInfo?.hasProgress || false,
      isCompleted: progressInfo?.isCompleted || false,
      completionRate: progressInfo?.completionRate || 0,
      stats: progressInfo?.stats || null,
    };
  });
});

// 修正：顯示角色扮演詳細資訊，只需傳遞ID
const showRolePlayDetails = (rolePlayId) => {
  selectedRolePlay.value = rolePlayId;
  document.body.style.overflow = "hidden"; // 防止背景滾動
};

// 關閉模態框
const closeModal = () => {
  selectedRolePlay.value = null;
  document.body.style.overflow = ""; // 恢復滾動
};

// 監視 activeTab 變化，當切換到 dialogs 時自動檢查進度
watch(activeTab, async (newTab) => {
  if (
    newTab === "dialogs" &&
    dialogs.value.length > 0 &&
    dialogProgressMap.value.size === 0
  ) {
    console.log("切換到 dialogs tab，自動檢查進度");
    await checkCurrentDialogsProgress();
  }
});

// 頁面載入時獲取數據並設置定期檢查
onMounted(() => {
  fetchData();

  // 設置定期檢查 token，每 10 分鐘檢查一次
  const tokenCheckInterval = setInterval(() => {
    // 如果 token 將在 10 分鐘內過期，提前更新
    if (
      tokenExpiryTime.value &&
      tokenExpiryTime.value - Date.now() < 10 * 60 * 1000
    ) {
      console.log("Token will expire soon, refreshing...");
      fetchAuthToken();
    }
  }, 10 * 60 * 1000);

  // 組件卸載時清除定時器
  onUnmounted(() => {
    clearInterval(tokenCheckInterval);
  });
});

// 開始角色扮演
const startRolePlay = async (rolePlay) => {
  try {
    // 確保 token 有效
    await fetchAuthToken();

    // 構建請求 URL 和參數
    const baseUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:5002";
    const params = new URLSearchParams();

    // 使用正確的用戶信息
    params.append(
      "user_id",
      userInfo.student.stuID || "STU-LzCBSvXvzuPfBSVpHnULbk"
    );
    params.append("name", userInfo.student.studentName || "chuchu");
    params.append("email", userInfo.student.email || "default@example.com");
    params.append("target", "roleplay");
    params.append("native_lang", "zh"); // 可根據應用程序設置調整
    params.append("site_lang", "en"); // 可根據應用程序設置調整

    // 添加角色扮演 ID
    params.append("roleplay_id", rolePlay.id);

    // 構建完整 URL
    const url = `${baseUrl}/external-login?${params.toString()}`;

    // 打開新視窗進行角色扮演
    window.open(url, "_blank", "width=1024,height=768");

    console.log(
      `Starting role play in new window: ${rolePlay.title} (ID: ${rolePlay.id})`
    );
    console.log("Role Play URL:", url);
  } catch (error) {
    console.error("Error starting role play:", error);
    alert("Failed to start role play. Please try again.");
  }
};

// 排序學習目標
const getSortedObjectives = (objectives) => {
  if (!objectives) return [];
  return [...objectives].sort((a, b) => a.sequence - b.sequence);
};

// 獲取角色扮演縮略圖
const getRolePlayThumbnail = (rolePlay) => {
  if (rolePlay.assets && rolePlay.assets.length > 0) {
    // 首先嘗試尋找更高質量的圖像資源
    // 根據 EnglishCentral API，可能有不同類型的資源
    // assetTypeId 107 通常是縮略圖，嘗試查找其他 assetTypeId
    const highResImage = rolePlay.assets.find(
      (asset) => asset.assetTypeId === 108 || asset.assetTypeId === 109
    );

    if (highResImage) {
      return highResImage.assetUrl;
    }

    // 如果沒有找到高分辨率圖片，使用縮略圖
    const thumbnail = rolePlay.assets.find(
      (asset) => asset.assetTypeId === 107
    );

    if (thumbnail) {
      // 嘗試增加圖片尺寸或用原始 URL
      // 有些 CDN 允許添加尺寸參數，如 ?width=800&height=600
      const originalUrl = thumbnail.assetUrl;

      // 首先檢查 URL 是否包含查詢參數
      if (originalUrl.includes("?")) {
        return `${originalUrl}&width=800&height=600`;
      } else {
        return `${originalUrl}?width=800&height=600`;
      }
    }
  }
  return "https://via.placeholder.com/150";
};

// 篩選條件
const rolePlaysFilters = ref({
  difficultyLevels: "1,2,3",
  start: 0,
  limit: 10,
});

const dialogsFilters = ref({
  difficulty: "1,2",
  page: 0,
  pageSize: 25,
  siteLanguage: "en",
  sortBy: 2,
  goalID: 4,
});

// 獲取角色扮演對話數據
const fetchRolePlays = async () => {
  try {
    // 確保有有效的 token
    const tokenResult = await fetchAuthToken();
    if (!tokenResult) {
      error.value =
        "Unable to obtain authorization token, please try again later.";
      return;
    }

    // 使用獲取到的 token 呼叫 API
    const response = await getEnglishCentralRolePlays(
      tokenResult.token, // 使用最新獲取的 token
      rolePlaysFilters.value.difficultyLevels,
      rolePlaysFilters.value.start,
      rolePlaysFilters.value.limit
    );

    // 檢查數據結構
    if (response && response.list) {
      rolePlays.value = response.list;
    } else {
      rolePlays.value = response || [];
    }

    console.log("getEnglishCentralRolePlays response = ", response);
    console.log("Role plays loaded:", rolePlays.value.length);
    console.log("Unique role plays:", uniqueRolePlays.value.length);
  } catch (err) {
    console.error("Error fetching role plays:", err);
    // 檢查是否為 token 相關錯誤
    if (
      err.message &&
      (err.message.includes("token") || err.message.includes("auth"))
    ) {
      // 強制刷新 token
      tokenExpiryTime.value = null;
      // 重試一次
      await fetchRolePlays();
    } else {
      error.value = "Failed to load role plays. Please try again.";
    }
  }
};

// 獲取一般對話數據
const fetchDialogs = async () => {
  try {
    // 確保有有效的 token
    const tokenResult = await fetchAuthToken();
    if (!tokenResult) {
      error.value =
        "Unable to obtain authorization token, please try again later.";
      return;
    }

    // 使用獲取到的 token 呼叫 API
    const response = await getEnglishCentralDialogs(
      tokenResult.token, // 使用最新獲取的 token
      dialogsFilters.value
    );

    dialogs.value = response || [];
    console.log("getEnglishCentralDialogs response = ", response);
    console.log("Dialogs loaded:", dialogs.value.length);

    // 在 dialogs tab 中自動檢查進度
    if (activeTab.value === "dialogs" && dialogs.value.length > 0) {
      await checkCurrentDialogsProgress();
    }
  } catch (err) {
    console.error("Error fetching dialogs:", err);
    // 檢查是否為 token 相關錯誤
    if (
      err.message &&
      (err.message.includes("token") || err.message.includes("auth"))
    ) {
      // 強制刷新 token
      tokenExpiryTime.value = null;
      // 重試一次
      await fetchDialogs();
    } else {
      error.value = "Failed to load dialogs. Please try again.";
    }
  }
};

// 獲取所有數據
const fetchData = async () => {
  loading.value = true;
  error.value = null;

  try {
    // 先獲取 token
    const tokenResult = await fetchAuthToken();
    if (!tokenResult) {
      error.value =
        "Unable to obtain authorization token, please try again later.";
      return;
    }

    // 獲取數據
    await Promise.all([fetchRolePlays(), fetchDialogs()]);
  } catch (err) {
    console.error("Error fetching data:", err);
    error.value = "Failed to load content. Please try again.";
  } finally {
    loading.value = false;
  }
};

// 格式化難度等級
const getDifficultyLabel = (level) => {
  const levels = {
    1: "Beginner",
    2: "Intermediate",
    3: "Advanced",
  };
  return levels[level] || "Unknown";
};

// 格式化時長
const formatDuration = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

// 添加新的方法
const startDialog = async (dialog) => {
  try {
    // 確保 token 有效
    await fetchAuthToken();

    // 構建請求 URL 和參數
    const baseUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:5002";
    const params = new URLSearchParams();

    // 使用正確的用戶信息
    params.append(
      "user_id",
      userInfo.student.stuID || "STU-LzCBSvXvzuPfBSVpHnULbk"
    );
    params.append("name", userInfo.student.studentName || "chuchu");
    params.append("email", userInfo.student.email || "default@example.com");
    params.append("target", "dialog");
    params.append("native_lang", "zh");
    params.append("site_lang", "en");
    params.append("dialog_id", dialog.dialogID.toString());

    // 構建完整 URL
    const url = `${baseUrl}/external-login?${params.toString()}`;

    // 打開新視窗
    window.open(url, "_blank", "width=1024,height=768");

    console.log(`Starting dialog: ${dialog.title} (ID: ${dialog.dialogID})`);
    console.log("Dialog URL:", url);
  } catch (error) {
    console.error("Error starting dialog:", error);
    alert("Failed to start dialog. Please try again.");
  }
};

const openDialogURL = (dialog) => {
  if (dialog.dialogURL) {
    window.open(dialog.dialogURL, "_blank");
  }
};

// 檢查 token 是否有效
const isTokenValid = () => {
  if (!authToken.value || !tokenExpiryTime.value) return false;
  return Date.now() < tokenExpiryTime.value;
};

// 單獨獲取 English Central 的授權令牌的函數
const fetchAuthToken = async () => {
  try {
    // 如果 token 仍然有效，直接返回現有 token
    if (isTokenValid()) {
      return {
        token: authToken.value,
        partnerKey: partnerKey.value,
      };
    }

    // 清除前一個錯誤信息
    tokenError.value = null;

    // 獲取用戶信息
    const userId = userInfo.student.stuID || "default-user";
    const name = userInfo.student.studentName || "Default User";
    const email = userInfo.student.email || "default@example.com";

    // 調用 API 獲取 token
    const response = await getEnglishCentralToken(
      userId,
      name,
      email,
      "zh", // native_lang
      "en" // site_lang
    );
    console.log("getEnglishCentralToken response = ", response);
    // 檢查響應數據
    if (response && response.success) {
      authToken.value = response.token;
      partnerKey.value = response.partner_key;
      // 設定 token 過期時間 (當前時間 + 55 分鐘，留 5 分鐘的緩衝)
      tokenExpiryTime.value = Date.now() + 55 * 60 * 1000;
      console.log("Authentication token obtained successfully");
      return {
        token: response.token,
        partnerKey: response.partner_key,
      };
    } else {
      throw new Error(response.message || "Failed to get token");
    }
  } catch (err) {
    console.error("Error fetching English Central token:", err);
    tokenError.value =
      "Unable to obtain authorization token, please try again later.";
    return null;
  }
};

// 新增：獲取課程進度數據
const fetchProgress = async () => {
  try {
    progressLoading.value = true;
    progressError.value = null;
    progressMessage.value = null;

    const response = await getCourseProgress(
      progressFilters.value.reportTypes,
      progressFilters.value.page,
      progressFilters.value.pageSize
    );

    if (response.status === "generating") {
      // 報告正在生成中
      progressMessage.value = response.message;
      progressData.value = [];

      // 設置定時器重試
      setTimeout(() => {
        fetchProgress();
      }, 60000); // 1分鐘後重試
    } else if (response.reports) {
      progressData.value = response.reports;
      progressMessage.value = null;
    } else {
      progressError.value = response.message || "Failed to load progress data";
    }
  } catch (error) {
    console.error("Error fetching progress:", error);
    progressError.value = "Network error: " + error.message;
  } finally {
    progressLoading.value = false;
  }
};

// 新增：獲取特定用戶進度
const fetchUserProgress = async (userId) => {
  try {
    progressLoading.value = true;
    progressError.value = null;
    progressMessage.value = null;

    console.log("🔍 正在查找用戶進度:", {
      userId: userId,
      currentUser: userInfo.student.studentName,
      stuID: userInfo.student.stuID,
    });

    const response = await getUserCourseProgress(
      userId,
      progressFilters.value.reportTypes
    );

    if (response.status === "generating") {
      // 報告正在生成中
      progressMessage.value = response.message;
      progressData.value = [];

      // 設置定時器重試
      setTimeout(() => {
        fetchUserProgress(userId);
      }, 60000); // 1分鐘後重試
    } else if (response.success) {
      progressData.value = [response.user_progress];
      progressMessage.value = null;
    } else {
      progressError.value = response.message || "Failed to load user progress";
    }
  } catch (error) {
    console.error("Error fetching user progress:", error);
    progressError.value = "Network error: " + error.message;
  } finally {
    progressLoading.value = false;
  }
};

// 新增：檢查對話進度
const checkDialogProgress = async (dialogId) => {
  try {
    // 確保有有效的 token
    const tokenResult = await fetchAuthToken();
    if (!tokenResult) {
      console.error("無法獲取認證令牌");
      return;
    }

    console.log(`檢查對話 ${dialogId} 的進度...`);

    // 獲取對話進度
    const progressData = await getDialogProgress(dialogId, tokenResult.token);
    console.log("對話進度:", progressData);

    // 獲取對話活動
    const activitiesData = await getDialogActivities(
      dialogId,
      tokenResult.token
    );
    console.log("對話活動:", activitiesData);
  } catch (error) {
    console.error("檢查對話進度時發生錯誤:", error);
  }
};

// 新增：獲取活動摘要
const fetchActivitySummary = async () => {
  try {
    activitySummaryLoading.value = true;
    activitySummaryError.value = null;

    const tokenResult = await fetchAuthToken();
    if (!tokenResult) {
      throw new Error("無法獲取認證令牌");
    }

    const data = await getActivitySummary(tokenResult.token);
    activitySummary.value = data[0]; // 返回第一個用戶的數據
  } catch (error) {
    console.error("獲取活動摘要錯誤:", error);
    activitySummaryError.value = error.message;
  } finally {
    activitySummaryLoading.value = false;
  }
};

// 新增：獲取我的英語視頻
const fetchMyEnglishVideos = async () => {
  try {
    myVideosLoading.value = true;
    myVideosError.value = null;

    const tokenResult = await fetchAuthToken();
    if (!tokenResult) {
      throw new Error("無法獲取認證令牌");
    }

    const data = await getMyEnglishVideos(
      tokenResult.token,
      videoFilters.value.page,
      videoFilters.value.pageSize,
      videoFilters.value.progress,
      videoFilters.value.sortBy
    );
    myVideos.value = data;
  } catch (error) {
    console.error("獲取我的英語視頻錯誤:", error);
    myVideosError.value = error.message;
  } finally {
    myVideosLoading.value = false;
  }
};

// 新增：獲取學生課程進度
const fetchStudentCourseProgress = async (accountId = 13673054) => {
  try {
    studentCourseProgressLoading.value = true;
    studentCourseProgressError.value = null;

    const tokenResult = await fetchAuthToken();
    if (!tokenResult) {
      throw new Error("無法獲取認證令牌");
    }

    const data = await getStudentCourseProgress(tokenResult.token, accountId);
    studentCourseProgress.value = data;
  } catch (error) {
    console.error("獲取學生課程進度錯誤:", error);
    studentCourseProgressError.value = error.message;
  } finally {
    studentCourseProgressLoading.value = false;
  }
};

// 新增：顯示所有用戶
const showAllUsers = async () => {
  try {
    const response = await getCourseProgress(
      progressFilters.value.reportTypes,
      progressFilters.value.page,
      progressFilters.value.pageSize
    );

    if (response.reports) {
      const userList = response.reports
        .map((user) => `${user.name} (ID: ${user.accountID})`)
        .join("\n");

      console.log("📋 所有可用用戶:", response.reports);
      alert(`所有可用用戶:\n${userList}`);
    } else {
      alert("無法獲取用戶列表");
    }
  } catch (error) {
    console.error("Error fetching all users:", error);
    alert("獲取用戶列表失敗: " + error.message);
  }
};

// 新增：獲取當前用戶進度
const getCurrentUserProgress = async () => {
  try {
    detailedProgressLoading.value = true;
    detailedProgressError.value = null;
    detailedProgress.value = null;
    dialogAnalysis.value = null;
    recentDialogActivity.value = [];

    const tokenResult = await fetchAuthToken();
    if (!tokenResult) {
      throw new Error("無法獲取認證令牌");
    }

    // 從 userInfo 獲取當前用戶的 ID
    const currentUserId = userInfo.student.stuID;
    if (!currentUserId) {
      throw new Error("無法獲取當前用戶ID");
    }

    console.log("使用統一API獲取當前用戶進度，用戶ID:", currentUserId);

    const data = await getUserDetailedProgress(
      tokenResult.token,
      currentUserId
    );
    detailedProgress.value = data;

    // 分析 Dialog 進度
    if (data.progress && data.progress.dialog_progress) {
      dialogAnalysis.value = analyzeDialogProgress(
        data.progress.dialog_progress
      );
      recentDialogActivity.value = getRecentDialogActivity(
        data.progress.dialog_progress,
        5
      );

      console.log("Dialog 進度分析:", dialogAnalysis.value);
      console.log("最近 Dialog 活動:", recentDialogActivity.value);
    }

    console.log("統一API返回的完整進度數據:", detailedProgress.value);
  } catch (error) {
    console.error("獲取當前用戶進度錯誤:", error);
    detailedProgressError.value = handleApiError(error, "獲取用戶進度");
  } finally {
    detailedProgressLoading.value = false;
  }
};

// 新增：檢查 Dialog 詳細進度
const checkDialogDetailedProgress = async () => {
  try {
    dialogDetailedLoading.value = true;
    dialogDetailedError.value = null;
    dialogDetailedProgress.value = [];
    dialogProgressAnalysis.value = null;

    const tokenResult = await fetchAuthToken();
    if (!tokenResult) {
      throw new Error("無法獲取認證令牌");
    }

    const dialogIds = testDialogList.value.map((d) => d.dialogID);
    console.log("開始檢查 Dialog 詳細進度，Dialog IDs:", dialogIds);

    // 批量獲取詳細進度，帶進度回調
    const progressResults = await getMultipleDialogDetailedProgress(
      tokenResult.token,
      dialogIds,
      (progress) => {
        console.log(
          `進度: ${progress.current}/${progress.total} - Dialog ${progress.dialogId}`,
          progress.result
        );
      }
    );

    dialogDetailedProgress.value = progressResults;

    // 分析結果
    const analysis = analyzeDialogProgressResults(
      progressResults,
      testDialogList.value
    );
    dialogProgressAnalysis.value = analysis;

    console.log("Dialog 詳細進度分析:", analysis);
  } catch (error) {
    console.error("檢查 Dialog 詳細進度錯誤:", error);
    dialogDetailedError.value = handleApiError(error, "檢查 Dialog 進度");
  } finally {
    dialogDetailedLoading.value = false;
  }
};

// 新增：檢查單個 Dialog 進度
const checkSingleDialogProgress = async (dialogId) => {
  try {
    const tokenResult = await fetchAuthToken();
    if (!tokenResult) {
      throw new Error("無法獲取認證令牌");
    }

    console.log(`檢查單個 Dialog 進度: ${dialogId}`);
    const result = await getDialogDetailedProgress(tokenResult.token, dialogId);

    console.log(`Dialog ${dialogId} 詳細進度:`, result);
    return result;
  } catch (error) {
    console.error(`檢查 Dialog ${dialogId} 進度錯誤:`, error);
    throw error;
  }
};

// 新增：兩步驟檢查 Dialogs 進度（Bridge API + Report API）
const checkCurrentDialogsProgress = async () => {
  if (dialogs.value.length === 0) return;

  try {
    dialogDetailedLoading.value = true;
    dialogDetailedError.value = null;

    const tokenResult = await fetchAuthToken();
    if (!tokenResult) {
      throw new Error("無法獲取認證令牌");
    }

    console.log("🚀 開始兩步驟檢查 Dialog 進度...");

    // 步驟 1: 獲取有進度的 Dialog ID 列表
    console.log("📋 步驟 1: 獲取有進度的 Dialog ID 列表...");
    const dialogsWithProgressIds = await getDialogsWithProgress(
      tokenResult.token
    );

    console.log(
      `✅ Bridge API 返回 ${dialogsWithProgressIds.length} 個有進度的 Dialog ID`
    );

    // 2. 獲取當前頁面 Dialog IDs
    const currentDialogIds = dialogs.value.map((d) => d.dialogID);

    // 3. 找出當前頁面中有進度的 Dialog ID
    const currentDialogsWithProgress = currentDialogIds.filter((id) =>
      dialogsWithProgressIds.includes(id)
    );

    console.log(
      `🎯 當前頁面中有進度的 Dialog: ${currentDialogsWithProgress.length}/${currentDialogIds.length}`
    );

    // 4. 清空並重新填充進度映射表
    dialogProgressMap.value.clear();

    // 步驟 2: 對有進度的 Dialog 獲取詳細信息
    if (currentDialogsWithProgress.length > 0) {
      console.log("📊 步驟 2: 獲取詳細進度信息...");

      const progressResults = await getMultipleDialogDetailedProgress(
        tokenResult.token,
        currentDialogsWithProgress,
        (progress) => {
          console.log(
            `Report API 進度: ${progress.current}/${progress.total} - Dialog ${progress.dialogId}`
          );
        }
      );

      // 存儲有進度的 Dialog 詳細信息
      progressResults.forEach((result) => {
        dialogProgressMap.value.set(result.dialogID, result);
      });

      console.log(`✅ 成功獲取 ${progressResults.length} 個 Dialog 的詳細進度`);
    }

    // 5. 為沒有進度的 Dialog 添加標記
    currentDialogIds.forEach((dialogId) => {
      if (!dialogsWithProgressIds.includes(dialogId)) {
        dialogProgressMap.value.set(dialogId, {
          dialogID: dialogId,
          hasProgress: false,
          totalPoints: 0,
          activities: [],
          message: "用戶尚未開始此對話",
          isCompleted: false,
          completionRate: 0,
          stats: {
            totalActivities: 0,
            completedActivities: 0,
            totalPoints: 0,
            earnedPoints: 0,
            correctAnswers: 0,
            totalAnswers: 0,
            accuracy: 0,
          },
        });
      }
    });

    console.log("✅ Dialog 進度檢查完成");
    console.log(
      `📈 總計: ${dialogProgressMap.value.size} 個 Dialog 已更新進度狀態`
    );
    console.log(
      `🔄 API 調用: 1 次 Bridge API + ${currentDialogsWithProgress.length} 次 Report API`
    );
  } catch (error) {
    console.error("檢查 Dialogs 進度錯誤:", error);
    dialogDetailedError.value = handleApiError(error, "檢查 Dialog 進度");
  } finally {
    dialogDetailedLoading.value = false;
  }
};

// 新增：獲取用戶完整進度（用於 Progress tab - 兩步驟版本）
const getCompleteProgress = async () => {
  try {
    detailedProgressLoading.value = true;
    detailedProgressError.value = null;
    detailedProgress.value = null;

    const tokenResult = await fetchAuthToken();
    if (!tokenResult) {
      throw new Error("無法獲取認證令牌");
    }

    console.log("🔍 使用兩步驟獲取用戶完整進度...");

    // 1. 獲取活動摘要
    console.log("📊 獲取活動摘要...");
    const activitySummary = await getUserActivitySummary(tokenResult.token);

    // 2. 使用兩步驟獲取所有有進度的 Dialog 詳細信息
    console.log("🚀 開始兩步驟獲取所有 Dialog 進度...");
    const dialogsWithProgressDetails = await getDialogsWithProgressDetails(
      tokenResult.token,
      "All", // 獲取所有進度類型
      0, // 第一頁
      500, // 更大的頁面大小確保獲取所有數據
      6, // LastViewed 排序
      (progress) => {
        console.log(
          `進度更新: ${progress.current}/${progress.total} - Dialog ${progress.dialogId}`
        );
      }
    );

    // 3. 獲取所有相關的 Dialog 詳細信息（包含標題）
    console.log("📋 獲取所有相關 Dialog 的詳細信息...");
    let allDialogsInfo = [...dialogs.value]; // 從當前頁面開始

    // 如果有進度的 dialogs 不在當前頁面中，需要獲取它們的信息
    const progressDialogIds = dialogsWithProgressDetails.map((d) => d.dialogID);
    const currentDialogIds = dialogs.value.map((d) => d.dialogID);
    const missingDialogIds = progressDialogIds.filter(
      (id) => !currentDialogIds.includes(id)
    );

    if (missingDialogIds.length > 0) {
      console.log(
        `🔍 需要額外獲取 ${missingDialogIds.length} 個 Dialog 的詳細信息`
      );
      try {
        const additionalDialogs = await getMultipleDialogInfo(
          tokenResult.token,
          missingDialogIds
        );
        allDialogsInfo.push(...additionalDialogs);
        console.log(
          `✅ 成功獲取額外的 ${additionalDialogs.length} 個 Dialog 信息`
        );
      } catch (error) {
        console.warn("獲取額外 Dialog 信息失敗:", error);
      }
    }

    console.log(
      `✅ 找到 ${dialogsWithProgressDetails.length} 個有進度的 Dialog`
    );

    // 4. 計算統計信息
    const totalDialogs = dialogsWithProgressDetails.length;
    const completedDialogs = dialogsWithProgressDetails.filter(
      (d) => d.isCompleted
    ).length;
    const totalPoints = dialogsWithProgressDetails.reduce(
      (sum, d) => sum + (d.totalPoints || 0),
      0
    );
    const totalEarnedPoints = dialogsWithProgressDetails.reduce(
      (sum, d) => sum + (d.earnedPoints || 0),
      0
    );

    // 5. 構建進度數據結構
    detailedProgress.value = {
      user_info: {
        name: userInfo.student.studentName,
        accountID: activitySummary?.accountID || "N/A",
        platform_id: userInfo.student.stuID,
        overallXP: activitySummary?.totalActivityPoints || totalEarnedPoints,
      },
      progress: {
        dialog_progress: dialogsWithProgressDetails.map((dialog) => ({
          dialogID: dialog.dialogID,
          dialogTitle: getDialogTitleById(dialog.dialogID, allDialogsInfo),
          progressPercentage: dialog.completionRate,
          totalPoints: dialog.totalPoints,
          earnedPoints: dialog.earnedPoints,
          lastActivity: dialog.lastActivity || new Date().toISOString(),
          isCompleted: dialog.isCompleted,
        })),
        total_dialogs: totalDialogs,
        completed_dialogs: completedDialogs,
        total_points: totalPoints,
        total_earned_points: totalEarnedPoints,
      },
      activity_summary: activitySummary,
      api_source: "Bridge + Report API", // 標記數據來源
    };

    // 6. 分析 Dialog 進度（使用轉換後的格式）
    if (detailedProgress.value.progress.dialog_progress) {
      dialogAnalysis.value = analyzeDialogProgress(
        detailedProgress.value.progress.dialog_progress
      );
      recentDialogActivity.value = getRecentDialogActivity(
        detailedProgress.value.progress.dialog_progress,
        5
      );
    }

    console.log("✅ 完整進度獲取成功 (兩步驟):");
    console.log(
      `📈 統計: ${completedDialogs}/${totalDialogs} 完成, ${totalEarnedPoints}/${totalPoints} 積分`
    );
    console.log(
      `🔄 API 調用: 1 次 Activity + 1 次 Bridge + ${totalDialogs} 次 Report`
    );
  } catch (error) {
    console.error("獲取完整進度錯誤:", error);
    detailedProgressError.value = handleApiError(error, "獲取完整進度");
  } finally {
    detailedProgressLoading.value = false;
  }
};

// 輔助函數：獲取活動類型名稱
const getActivityTypeName = (activityType) => {
  const typeMap = {
    watchActivity: "觀看",
    speakActivity: "說話",
    learnActivity: "學習",
    quizActivity: "測驗",
    reviewActivity: "複習",
  };
  return typeMap[activityType] || activityType;
};
</script>

<style scoped>
/* 頁面基本樣式，防止水平滾動 */
:deep(body),
:deep(html) {
  max-width: 100%;
  overflow-x: hidden;
}

/* 確保所有元素不會超出容器寬度 */
* {
  box-sizing: border-box;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

@media (max-width: 992px) {
  :deep(.top-toolbar.navbar-desktop) {
    position: relative !important;
    /* 其他樣式 */
  }
}

.english-central-container {
  /* max-width: 1200px; */
  margin: 0 auto;
  padding: 22px 60px;
  font-family: Arial, sans-serif;
  background-image: url("@/assets/img/background/bg-player-2.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  min-height: 100vh;
  max-width: 100vw;
  overflow-x: hidden;
}

@media (max-width: 992px) {
  .english-central-container {
    padding-top: 81px;
    padding-left: 20px;
    padding-right: 20px;
  }
}

@media (max-width: 576px) {
  .english-central-container {
    padding: 81px 15px 15px 15px;
  }

  .tabs-container {
    flex-direction: row;
    width: 100%;
  }

  .tab {
    flex: 1;
    text-align: center;
    padding: 12px 10px;
    font-size: 14px;
  }

  .filter-group {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }

  .filter-group select {
    width: 100%;
    margin-top: 5px;
  }

  .filters {
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }

  .cards-container {
    grid-template-columns: 1fr;
  }
}

.page-title {
  text-align: center;
  margin-bottom: 30px;
  color: #333;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #3498db;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-message {
  text-align: center;
  color: #e74c3c;
  padding: 20px;
  background-color: #fdf0f0;
  border-radius: 5px;
  margin-bottom: 20px;
}

.retry-button {
  background-color: #3498db;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 10px;
}

/* .content-section {
  margin-bottom: 40px;
  background-color: #ffffff;
  padding: 20px;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  border-top: none;
} */

.content-section h2 {
  margin-bottom: 20px;
  border-bottom: 2px solid #3498db;
  padding-bottom: 10px;
  color: #2c3e50;
}

.filters {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-group select {
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: white;
}

.cards-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.content-card {
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  /* min-height: 440px; 使用最小高度而不是固定高度 */
}

.content-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

.card-image {
  height: 180px;
  overflow: hidden;
  position: relative;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.difficulty-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 5px 10px;
  border-radius: 20px;
  color: white;
  font-weight: bold;
  font-size: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.level-1 {
  background-color: #4caf50; /* 綠色 - 初級 */
}

.level-2 {
  background-color: #ff9800; /* 橙色 - 中級 */
}

.level-3 {
  background-color: #f44336; /* 紅色 - 高級 */
}

.card-content {
  padding: 20px;
}

.card-title {
  margin: 0 0 12px 0;
  font-size: 18px;
  color: #333;
  font-weight: bold;
  min-height: 44px; /* 使用最小高度 */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.card-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 14px;
  color: #666;
  min-height: 24px; /* 使用最小高度 */
}

.description {
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
  line-height: 1.4;
  min-height: 60px; /* 使用最小高度 */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.roles-container {
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
  margin-top: 12px;
  margin-bottom: 15px;
  font-size: 14px;
  min-height: 80px; /* 使用最小高度 */
}

.role-item {
  margin-bottom: 6px;
}

.role-item:last-child {
  margin-bottom: 0;
}

.role-label {
  font-weight: bold;
  color: #555;
  margin-right: 5px;
}

.role-name {
  color: #666;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 15px;
  min-height: 40px; /* 使用最小高度 */
}

.view-details-btn,
.start-btn {
  padding: 8px 10px;
  border-radius: 4px;
  font-size: 14px;
  border: none;
  cursor: pointer;
  flex: 1;
  transition: all 0.2s ease;
}

.view-details-btn {
  background-color: #e0e0e0;
  color: #333;
}

.view-details-btn:hover {
  background-color: #e0e0e0;
}

.start-btn {
  background-color: #3498db;
  color: white;
}

.start-btn:hover {
  background-color: #2980b9;
}

/* 詳細資訊模態框樣式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  padding: 25px;
}

.close-modal {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.modal-content h2 {
  margin-top: 0;
  margin-bottom: 10px;
  color: #333;
  padding-right: 30px;
}

.difficulty-tag {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 20px;
  color: white;
  font-size: 14px;
  margin-bottom: 20px;
}

.modal-img-container {
  width: 100%;
  height: 250px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
}

.modal-img-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.modal-info h3 {
  margin: 20px 0 10px;
  color: #444;
  font-size: 18px;
}

.modal-info p {
  margin: 0 0 15px;
  color: #666;
  line-height: 1.5;
}

.roles-detail {
  display: flex;
  gap: 20px;
  margin: 15px 0;
}

.role-detail-item {
  flex: 1;
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.role-detail-item h3 {
  margin-top: 0;
  font-size: 16px;
}

.message-bubble {
  background-color: #e1f5fe;
  border-radius: 10px;
  padding: 12px;
  position: relative;
  border-top-left-radius: 0;
}

.candos-section {
  margin-top: 25px;
  border-top: 1px solid #eee;
  padding-top: 20px;
}

.cando-name {
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
}

.objectives-list {
  margin: 0;
  padding-left: 20px;
}

.objectives-list li {
  margin-bottom: 8px;
  color: #555;
}

.modal-actions {
  margin-top: 30px;
  text-align: center;
}

.start-btn-large {
  background-color: #3498db;
  color: white;
  padding: 12px 30px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: bold;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.start-btn-large:hover {
  background-color: #2980b9;
}

/* 響應式調整 */
@media (max-width: 768px) {
  .roles-detail {
    flex-direction: column;
    gap: 10px;
  }

  .modal-content {
    padding: 20px;
    width: 95%;
  }

  .view-details-btn,
  .start-btn {
    padding: 8px 5px;
    font-size: 13px;
  }

  .card-content h3 {
    font-size: 16px;
  }

  .card-image {
    height: 150px;
  }
}

@media (max-width: 576px) {
  .modal-content {
    padding: 15px;
    width: 100%;
    margin: 0 10px;
  }

  .modal-img-container {
    height: 180px;
  }

  .modal-content h2 {
    font-size: 20px;
  }

  .modal-info h3 {
    font-size: 16px;
  }

  .start-btn-large {
    width: 100%;
  }

  .content-card {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  }

  .card-footer {
    flex-direction: column;
    gap: 8px;
  }
}

.preview-image {
  margin: 10px 0;
  border-radius: 8px;
  overflow: hidden;
}

.demo-picture {
  width: 100%;
  height: auto;
  object-fit: cover;
}

.promotional-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: #ff6b6b;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
}

.tabs-container {
  display: flex;
  background-color: #f5f5f5;
  border-radius: 8px 8px 0 0;
  overflow: hidden;
  margin-bottom: 0;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.tab {
  padding: 15px 25px;
  font-size: 16px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 3px solid transparent;
  background-color: #f5f5f5;
  position: relative;
}

.tab:hover {
  background-color: #e9e9e9;
  color: #333;
}

.tab.active {
  color: #3498db;
  border-bottom-color: #3498db;
  background-color: white;
}

.tab.active:after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: #3498db;
}

.content-section {
  margin-bottom: 40px;
  background-color: #fffffff2;
  padding: 20px;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  border-top: none;
}
.card-content-roleplay {
  height: 100%;
  min-height: 350px;
}
@media (max-width: 1200px) {
  .card-content-roleplay {
    min-height: 320px;
  }
}

/* 新增：進度區塊的樣式 */
.progress-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.progress-card {
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.progress-card:hover {
  transform: translateY(-5px);
}

.user-info {
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 15px;
  margin-bottom: 20px;
}

.user-info h3 {
  margin: 0 0 10px 0;
  color: #333;
}

.user-info p {
  margin: 5px 0;
  color: #666;
  font-size: 14px;
}

.progress-report {
  margin-bottom: 20px;
}

.progress-report h4 {
  margin: 0 0 15px 0;
  color: #555;
  font-size: 16px;
}

.course-item {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 10px;
}

.course-name {
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e1e5e9;
  border-radius: 4px;
  overflow: hidden;
  margin: 8px 0;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 14px;
  color: #666;
  text-align: right;
}

.xp-points {
  background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
  padding: 15px;
  border-radius: 8px;
  text-align: center;
}

.xp-value {
  font-size: 2em;
  font-weight: 700;
  color: #e67e22;
}

.btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-right: 10px;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
}

/* 新增：生成中提示的樣式 */
.generating-message {
  text-align: center;
  color: #666;
  padding: 40px 20px;
  background-color: #f8f9fa;
  border-radius: 10px;
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  border: 2px dashed #dee2e6;
}

.generating-icon {
  font-size: 48px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.generating-message h5 {
  margin: 0;
  color: #495057;
  font-size: 18px;
}

.generating-message p {
  margin: 0;
  color: #6c757d;
  font-size: 14px;
}

/* 新增：活動摘要和我的英語視頻的樣式 */
.activity-summary-section,
.my-videos-section,
.student-course-section {
  margin-top: 20px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.activity-summary-section h3,
.my-videos-section h3,
.student-course-section h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #333;
  font-size: 18px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
}

.summary-card {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.summary-card .label {
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
}

.summary-card .value {
  font-size: 24px;
  font-weight: bold;
  color: #3498db;
}

.video-filters,
.course-filters {
  margin-bottom: 15px;
  text-align: right;
}

.video-filters select,
.course-filters select {
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: #ffffff;
  font-size: 14px;
}

.video-grid,
.course-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.video-card,
.course-card {
  background-color: #ffffff;
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.video-card h4,
.course-card h4 {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 16px;
  color: #333;
  font-weight: 600;
}

.video-progress,
.course-progress {
  width: 100%;
  height: 8px;
  background: #e1e5e9;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.video-progress .progress-fill,
.course-progress .progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
}

.video-meta,
.course-meta {
  font-size: 12px;
  color: #666;
  margin-top: 8px;
}

.video-card .progress-text,
.course-card .progress-text {
  font-size: 14px;
  color: #666;
  text-align: right;
}

/* 新增：進度區塊的樣式 */
.progress-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.progress-card {
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.progress-card:hover {
  transform: translateY(-5px);
}

.user-info {
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 15px;
  margin-bottom: 20px;
}

.user-info h3 {
  margin: 0 0 10px 0;
  color: #333;
}

.user-info p {
  margin: 5px 0;
  color: #666;
  font-size: 14px;
}

.progress-report {
  margin-bottom: 20px;
}

.progress-report h4 {
  margin: 0 0 15px 0;
  color: #555;
  font-size: 16px;
}

.course-item {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 10px;
}

.course-name {
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e1e5e9;
  border-radius: 4px;
  overflow: hidden;
  margin: 8px 0;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 14px;
  color: #666;
  text-align: right;
}

.xp-points {
  background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
  padding: 15px;
  border-radius: 8px;
  text-align: center;
}

.xp-value {
  font-size: 2em;
  font-weight: 700;
  color: #e67e22;
}

.btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-right: 10px;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
}

/* 新增：生成中提示的樣式 */
.generating-message {
  text-align: center;
  color: #666;
  padding: 40px 20px;
  background-color: #f8f9fa;
  border-radius: 10px;
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  border: 2px dashed #dee2e6;
}

.generating-icon {
  font-size: 48px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.generating-message h5 {
  margin: 0;
  color: #495057;
  font-size: 18px;
}

.generating-message p {
  margin: 0;
  color: #6c757d;
  font-size: 14px;
}

.detailed-progress-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.detailed-progress-section h4 {
  color: #495057;
  margin-bottom: 20px;
  font-weight: 600;
}

.progress-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.progress-summary {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #dee2e6;
}

.progress-summary h5 {
  color: #495057;
  margin-bottom: 15px;
  font-weight: 600;
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.stat-item .label {
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 5px;
}

.stat-item .value {
  font-size: 18px;
  font-weight: 600;
  color: #495057;
}

.dialog-progress,
.course-progress {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #dee2e6;
}

.dialog-progress h5,
.course-progress h5 {
  color: #495057;
  margin-bottom: 15px;
  font-weight: 600;
}

.dialog-list,
.course-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dialog-item,
.course-item {
  background: white;
  border-radius: 6px;
  padding: 12px;
  border: 1px solid #e9ecef;
}

.dialog-item h6,
.course-item h6 {
  color: #495057;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 14px;
}

.dialog-stats,
.course-stats {
  display: flex;
  gap: 15px;
  font-size: 12px;
  color: #6c757d;
}

/* 新增：Dialog 進度分析樣式 */
.user-basic-info {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #dee2e6;
}

.user-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 15px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.info-item .label {
  font-weight: 600;
  color: #495057;
}

.info-item .value {
  color: #6c757d;
  font-weight: 500;
}

.dialog-analysis-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.analysis-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 15px;
  margin-top: 15px;
}

.stat-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.stat-card.completed {
  border-color: #28a745;
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
}

.stat-card.in-progress {
  border-color: #ffc107;
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
}

.stat-card.completion-rate {
  border-color: #007bff;
  background: linear-gradient(135deg, #cce5ff 0%, #b3d9ff 100%);
}

.stat-card.points {
  border-color: #6f42c1;
  background: linear-gradient(135deg, #e2d9f3 0%, #d1c4e9 100%);
}

.stat-card.points-rate {
  border-color: #fd7e14;
  background: linear-gradient(135deg, #ffe8d1 0%, #ffd3a5 100%);
}

.stat-number {
  font-size: 1.8em;
  font-weight: 700;
  color: #333;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 0.9em;
  color: #6c757d;
  font-weight: 500;
}

.recent-dialog-activity {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.activity-list {
  margin-top: 15px;
}

.activity-item {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 10px;
  border-left: 4px solid #007bff;
  transition: all 0.3s ease;
}

.activity-item:hover {
  transform: translateX(5px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.activity-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.activity-title {
  margin: 0;
  color: #333;
  font-size: 1em;
  font-weight: 600;
}

.activity-id {
  font-size: 0.8em;
  color: #6c757d;
  background: #e9ecef;
  padding: 2px 8px;
  border-radius: 12px;
}

.activity-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.activity-progress .progress-bar {
  flex: 1;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
}

.activity-progress .progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
  transition: width 0.3s ease;
}

.activity-progress .progress-text {
  font-size: 0.9em;
  font-weight: 600;
  color: #495057;
  min-width: 40px;
}

.activity-meta {
  display: flex;
  justify-content: space-between;
  font-size: 0.8em;
  color: #6c757d;
}

.activity-points {
  font-weight: 600;
  color: #28a745;
}

.activity-date {
  font-style: italic;
}

/* 響應式設計 */
@media (max-width: 768px) {
  .summary-stats {
    grid-template-columns: 1fr;
  }

  .dialog-stats,
  .course-stats {
    flex-direction: column;
    gap: 5px;
  }

  .user-info-grid {
    grid-template-columns: 1fr;
  }

  .analysis-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .activity-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }

  .activity-meta {
    flex-direction: column;
    gap: 3px;
  }
}

@media (max-width: 480px) {
  .analysis-stats {
    grid-template-columns: 1fr;
  }
}

/* Dialog 詳細進度樣式 */
.dialog-detailed-progress-section {
  background: white;
  border-radius: 12px;
  padding: 25px;
  margin: 20px 0;
  border: 1px solid #e9ecef;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.dialog-detailed-progress-section h3 {
  color: #495057;
  margin-bottom: 20px;
  font-weight: 700;
  border-bottom: 2px solid #007bff;
  padding-bottom: 10px;
}

.dialog-analysis-overview {
  margin-bottom: 30px;
}

.overview-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-top: 15px;
}

.stat-card.accuracy {
  border-color: #20c997;
  background: linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%);
}

.dialog-detailed-list h4 {
  color: #495057;
  margin-bottom: 20px;
  font-weight: 600;
}

.dialog-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 20px;
}

.dialog-detail-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  position: relative;
}

.dialog-detail-card.has-progress {
  border-color: #007bff;
  background: linear-gradient(135deg, #fff 0%, #f0f8ff 100%);
}

.dialog-detail-card.completed {
  border-color: #28a745;
  background: linear-gradient(135deg, #fff 0%, #f0fff4 100%);
}

.dialog-detail-card.no-progress {
  border-color: #6c757d;
  background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
}

.dialog-detail-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

.dialog-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.dialog-card-title {
  margin: 0;
  color: #333;
  font-size: 1.1em;
  font-weight: 600;
  flex: 1;
}

.dialog-id-badge {
  background: #007bff;
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: 500;
}

.dialog-card-content {
  margin-top: 15px;
}

.dialog-completion {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.completion-bar {
  flex: 1;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.completion-fill {
  height: 100%;
  background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
  transition: width 0.3s ease;
}

.completion-text {
  font-size: 0.9em;
  font-weight: 600;
  color: #495057;
  min-width: 70px;
}

.dialog-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 15px;
  padding: 15px;
  background: rgba(0, 123, 255, 0.05);
  border-radius: 8px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-label {
  font-size: 0.9em;
  color: #6c757d;
  font-weight: 500;
}

.stat-value {
  font-size: 0.9em;
  color: #495057;
  font-weight: 600;
}

.activities-summary {
  margin-top: 15px;
}

.activities-summary h6 {
  margin: 0 0 10px 0;
  color: #495057;
  font-size: 0.95em;
  font-weight: 600;
}

.activity-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.activity-item {
  display: grid;
  grid-template-columns: 1fr 1fr 60px;
  gap: 10px;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 6px;
  border-left: 3px solid #dee2e6;
  transition: all 0.2s ease;
}

.activity-item.completed {
  border-left-color: #28a745;
  background: rgba(40, 167, 69, 0.1);
}

.activity-type {
  font-size: 0.85em;
  color: #495057;
  font-weight: 500;
}

.activity-progress {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mini-progress-bar {
  flex: 1;
  height: 4px;
  background: #e9ecef;
  border-radius: 2px;
  overflow: hidden;
}

.mini-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
  transition: width 0.3s ease;
}

.activity-percentage {
  font-size: 0.75em;
  color: #6c757d;
  min-width: 35px;
  text-align: right;
}

.activity-points {
  font-size: 0.8em;
  color: #28a745;
  font-weight: 600;
  text-align: center;
}

.dialog-no-progress {
  text-align: center;
  padding: 30px 20px;
  color: #6c757d;
}

.no-progress-icon {
  font-size: 3em;
  margin-bottom: 15px;
  opacity: 0.7;
}

.dialog-no-progress p {
  margin: 0 0 20px 0;
  font-size: 0.95em;
}

.start-dialog-btn {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9em;
  font-weight: 600;
  transition: all 0.3s ease;
}

.start-dialog-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
}

/* 響應式設計 */
@media (max-width: 768px) {
  .dialog-cards {
    grid-template-columns: 1fr;
  }

  .overview-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .dialog-stats {
    grid-template-columns: 1fr;
  }

  .activity-item {
    grid-template-columns: 1fr;
    gap: 5px;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .overview-stats {
    grid-template-columns: 1fr;
  }

  .dialog-card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}

/* 新增：Dialog 卡片進度樣式 */
.progress-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 15px;
  background: rgba(0, 123, 255, 0.1);
  border-radius: 8px;
  margin-bottom: 20px;
  color: #0056b3;
  font-weight: 500;
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 123, 255, 0.2);
  border-radius: 50%;
  border-top: 2px solid #007bff;
  animation: spin 1s linear infinite;
}

.dialog-card {
  position: relative;
  transition: all 0.3s ease;
}

.dialog-card.has-progress {
  border-left: 4px solid #007bff;
  background: linear-gradient(135deg, #fff 0%, #f0f8ff 100%);
}

.dialog-card.completed {
  border-left: 4px solid #28a745;
  background: linear-gradient(135deg, #fff 0%, #f0fff4 100%);
}

.dialog-card.no-progress {
  border-left: 4px solid #6c757d;
  opacity: 0.8;
}

.progress-badge {
  position: absolute;
  top: 50px;
  left: 10px;
  z-index: 2;
}

.completed-badge {
  background: rgba(40, 167, 69, 0.9);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75em;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.in-progress-badge {
  background: rgba(0, 123, 255, 0.9);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75em;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.not-started-badge {
  background: rgba(108, 117, 125, 0.9);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75em;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.dialog-progress-info {
  margin: 10px 0;
  padding: 10px;
  background: rgba(0, 123, 255, 0.05);
  border-radius: 6px;
  border-left: 3px solid #007bff;
}

.no-progress-status {
  text-align: center;
  padding: 5px 0;
}

.status-text {
  color: #6c757d;
  font-size: 0.85em;
  font-weight: 500;
  background: rgba(108, 117, 125, 0.1);
  padding: 4px 12px;
  border-radius: 12px;
  border: 1px solid rgba(108, 117, 125, 0.2);
}

.progress-bar-mini {
  width: 100%;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill-mini {
  height: 100%;
  background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
  transition: width 0.3s ease;
}

.progress-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 0.8em;
}

.progress-stats .stat {
  color: #495057;
  font-weight: 500;
  padding: 2px 8px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  border: 1px solid rgba(0, 123, 255, 0.2);
}

.continue-btn {
  background: linear-gradient(135deg, #ffc107 0%, #ffb300 100%);
  color: #212529;
}

.continue-btn:hover {
  background: linear-gradient(135deg, #ffb300 0%, #ff8f00 100%);
  color: #212529;
}

/* 響應式設計調整 */
@media (max-width: 768px) {
  .progress-badge {
    top: 40px;
    left: 5px;
  }

  .completed-badge,
  .in-progress-badge,
  .not-started-badge {
    font-size: 0.7em;
    padding: 3px 6px;
  }

  .progress-stats {
    flex-direction: column;
    gap: 5px;
  }
}

@media (max-width: 480px) {
  .progress-loading {
    flex-direction: column;
    text-align: center;
  }
}
</style>
